import { DangerTag, NeutralTag, PrimaryTag, Text } from "@certn/cascadia-react";
import { CascadiaJsonFormContext } from "@certn/forms";
import { ActionListControl } from "@certn/forms/src/controls/ActionListControl";
import { formatDate } from "@certn/utils";
import { ArrayControlProps } from "@jsonforms/core";
import { useTranslation } from "react-i18next";

import { useApplication } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { updateSection, removeItems, replaceOrAddItems } from "../../utils";
import { useContext } from "react";

export function EmploymentHistoryControl(props: ArrayControlProps) {
  const { caseId } = useApplicationFormContext();
  const { currentSection, basePath } = useApplication();
  const { t } = useTranslation();
  const { setIsSubmitted } = useContext(CascadiaJsonFormContext);
  const dataSection = "employment-history";

  const contactableEmployers = props?.data
    ? props?.data.filter(
        ({ do_not_contact_employer }: { do_not_contact_employer: boolean }) =>
          !do_not_contact_employer
      ).length
    : 0;

  return (
    <ActionListControl
      {...props}
      isNextPageDisabled={(values) => !values?.employment_type}
      alwaysDisplayRequirementsAsBullets={
        props?.uischema?.options?.alwaysDisplayRequirementsAsBullets ?? false
      }
      filteredTotal={contactableEmployers}
      renderTitle={(item) => {
        if (currentSection === "reference-history") {
          return `${item.first_name} ${item.last_name} - ${item.email}`;
        }

        return item.company_name;
      }}
      renderTag={({ end_date, do_not_contact_employer, employment_type }) => {
        return (
          <>
            {!end_date ? (
              <PrimaryTag type="subtle">{t("Current")}</PrimaryTag>
            ) : null}
            {employment_type === "SELF_EMPLOYED" ? (
              <NeutralTag type="subtle">{t("Self-employed")}</NeutralTag>
            ) : null}
            {do_not_contact_employer ? (
              <DangerTag type="subtle">{t("Do not contact")}</DangerTag>
            ) : null}
          </>
        );
      }}
      renderSubtitle={(item) => {
        const { start_date, end_date } = item;
        return (
          <>
            {start_date && (
              <Text style="global/body-secondary" color="text-weak">
                {[start_date, end_date]
                  .map((date) => formatDate(date, "MMM yyyy", t("Present")))
                  .join(" - ")}
              </Text>
            )}
          </>
        );
      }}
      onItemChanged={async (data, values, index) => {
        setIsSubmitted?.(false);
        const updatedArray = replaceOrAddItems(
          dataSection,
          data,
          values,
          index
        );
        if (caseId) {
          await updateSection(
            caseId,
            updatedArray,
            currentSection,
            false,
            basePath
          );
        }
      }}
      onItemRemoved={async (parentValues, indexes) => {
        const updatedArray = removeItems(dataSection, parentValues, indexes);
        if (updatedArray) {
          if (caseId) {
            await updateSection(
              caseId,
              updatedArray,
              currentSection,
              false,
              basePath
            );
          }
        }
      }}
    />
  );
}
